import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Bot, MoreHorizontal, Pin, Plus, Search } from 'lucide-react';
import Breadcrumbs from '../components/Breadcrumbs';
import Modal from '../components/Modal';
import ColorPicker from '../components/ColorPicker';
import SelectMenu from '../components/SelectMenu';
import VisibilityIndicator from '../components/VisibilityIndicator';
import { apiClient } from '../apiClient';
import { ProjectCard, FolderCard } from './ProjectsPage';

const SORT_OPTIONS = {
  projects: [
    { value: 'updated_desc', label: 'Recently Updated', description: 'Newest activity first.' },
    { value: 'updated_asc', label: 'Least Recently Updated', description: 'Oldest activity first.' },
    { value: 'created_desc', label: 'Date Created (Newest)', description: 'Latest created first.' },
    { value: 'created_asc', label: 'Date Created (Oldest)', description: 'Earliest created first.' },
    { value: 'name_asc', label: 'Alphabetical (A-Z)', description: 'Sort by name ascending.' },
    { value: 'name_desc', label: 'Alphabetical (Z-A)', description: 'Sort by name descending.' },
    { value: 'color_asc', label: 'Color (A-Z)', description: 'Sort by accent color.' },
    { value: 'color_desc', label: 'Color (Z-A)', description: 'Sort by accent color, reverse.' }
  ],
  folders: [
    { value: 'updated_desc', label: 'Recently Updated', description: 'Newest activity first.' },
    { value: 'updated_asc', label: 'Least Recently Updated', description: 'Oldest activity first.' },
    { value: 'created_desc', label: 'Date Created (Newest)', description: 'Latest created first.' },
    { value: 'created_asc', label: 'Date Created (Oldest)', description: 'Earliest created first.' },
    { value: 'name_asc', label: 'Alphabetical (A-Z)', description: 'Sort by name ascending.' },
    { value: 'name_desc', label: 'Alphabetical (Z-A)', description: 'Sort by name descending.' },
    { value: 'color_asc', label: 'Color (A-Z)', description: 'Sort by accent color.' },
    { value: 'color_desc', label: 'Color (Z-A)', description: 'Sort by accent color, reverse.' }
  ],
  instructions: [
    { value: 'updated_desc', label: 'Recently Updated', description: 'Newest activity first.' },
    { value: 'updated_asc', label: 'Least Recently Updated', description: 'Oldest activity first.' },
    { value: 'created_desc', label: 'Date Created (Newest)', description: 'Latest created first.' },
    { value: 'created_asc', label: 'Date Created (Oldest)', description: 'Earliest created first.' },
    { value: 'name_asc', label: 'Alphabetical (A-Z)', description: 'Sort by name ascending.' },
    { value: 'name_desc', label: 'Alphabetical (Z-A)', description: 'Sort by name descending.' }
  ]
};

const DEFAULT_FORM_STATE = {
  name: '',
  description: '',
  category_id: '',
  color: '#3B82F6',
  visibility: 'private'
};

const getDateValue = (value) => {
  if (!value) return 0;
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const sortByOption = (items, sortKey) => {
  const sorted = [...items];
  const byName = (a, b) => (a.name || '').localeCompare(b.name || '');
  const byColor = (a, b) => (a.color || '').localeCompare(b.color || '');
  const byCreated = (a, b) => getDateValue(a.created_at) - getDateValue(b.created_at);
  const byUpdated = (a, b) => getDateValue(a.updated_at) - getDateValue(b.updated_at);

  switch (sortKey) {
    case 'name_asc':
      return sorted.sort(byName);
    case 'name_desc':
      return sorted.sort((a, b) => byName(b, a));
    case 'created_asc':
      return sorted.sort(byCreated);
    case 'created_desc':
      return sorted.sort((a, b) => byCreated(b, a));
    case 'updated_asc':
      return sorted.sort(byUpdated);
    case 'updated_desc':
      return sorted.sort((a, b) => byUpdated(b, a));
    case 'color_asc':
      return sorted.sort(byColor);
    case 'color_desc':
      return sorted.sort((a, b) => byColor(b, a));
    default:
      return sorted.sort((a, b) => byUpdated(b, a));
  }
};

const splitPinned = (items, idKey) => {
  const pinnedItems = items
    .filter((item) => item.pinned_at)
    .sort((a, b) => getDateValue(a.pinned_at) - getDateValue(b.pinned_at));
  const pinnedIds = new Set(pinnedItems.map((item) => item[idKey]));
  const unpinned = items.filter((item) => !pinnedIds.has(item[idKey]));
  return { pinnedItems, unpinned };
};

const formatDate = (value) => {
  if (!value) return '--';
  try {
    return new Date(value).toLocaleDateString();
  } catch (error) {
    return '--';
  }
};

function EmptyState({ title, description, actionLabel, onAction }) {
  return (
    <div className="empty-state card-glass">
      <h3 className="text-xl font-bold mb-2">{title}</h3>
      <p className="text-gray-400 mb-4">{description}</p>
      {onAction && (
        <button className="btn-primary" type="button" onClick={onAction}>
          {actionLabel}
        </button>
      )}
    </div>
  );
}

function ActionMenu({ pinned, onEdit, onTogglePin, onDelete }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClick = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <div className="action-menu" ref={ref}>
      <button
        type="button"
        className="icon-btn menu-button"
        onClick={(event) => {
          event.stopPropagation();
          setOpen((prev) => !prev);
        }}
        aria-label="Open menu"
      >
        <MoreHorizontal className="w-4 h-4" />
      </button>
      {open && (
        <div className="action-menu-list">
          <button type="button" onClick={onEdit}>Edit</button>
          <button type="button" onClick={onTogglePin}>{pinned ? 'Unpin' : 'Pin'}</button>
          <button type="button" className="danger" onClick={onDelete}>Delete</button>
        </div>
      )}
    </div>
  );
}

function InstructionCard({ instruction, onOpen, onEdit, onTogglePin, onDelete }) {
  return (
    <div
      className="tile-card"
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onOpen();
        }
      }}
    >
      <div className="tile-card-header">
        <div className="tile-icon" style={{ color: '#22d3ee' }}>
          <Bot className="w-6 h-6" />
        </div>
        <div className="tile-card-actions">
          <VisibilityIndicator visibility={instruction.visibility || 'private'} />
          {instruction.pinned_at && (
            <div className="pin-indicator" data-tooltip="Pinned" aria-label="Pinned">
              <Pin className="w-3 h-3" />
            </div>
          )}
          <ActionMenu
            pinned={!!instruction.pinned_at}
            onEdit={(event) => {
              event?.stopPropagation?.();
              onEdit();
            }}
            onTogglePin={(event) => {
              event?.stopPropagation?.();
              onTogglePin();
            }}
            onDelete={(event) => {
              event?.stopPropagation?.();
              onDelete();
            }}
          />
        </div>
      </div>
      <h3 className="tile-title">{instruction.name}</h3>
      <p className="tile-sub">{instruction.description || 'No description yet.'}</p>
      <div className="tile-meta flex items-center gap-2 flex-wrap">
        <span>Updated {formatDate(instruction.updated_at)}</span>
        {instruction.live_enabled && (
          <span className="text-xs px-2 py-0.5 rounded bg-green-500/20 text-green-300 border border-green-500/30">
            Live
          </span>
        )}
        {instruction.dev_enabled && !instruction.live_enabled && (
          <span className="text-xs px-2 py-0.5 rounded bg-yellow-500/20 text-yellow-300 border border-yellow-500/30">
            Dev
          </span>
        )}
      </div>
    </div>
  );
}

function WorkspaceHeader({ breadcrumbs, title, description, search, setSearch, sort, setSort, sortOptions, actionLabel, onAction }) {
  return (
    <div className="page-header">
      <div className="page-header-top">
        <div>
          <Breadcrumbs items={breadcrumbs} />
          <h1 className="text-3xl font-bold mt-3">{title}</h1>
          <p className="text-gray-400 text-sm mt-1">{description}</p>
        </div>
        {onAction && (
          <button className="btn-primary" type="button" onClick={onAction}>
            <Plus className="w-4 h-4" /> {actionLabel}
          </button>
        )}
      </div>
      <div className="page-header-actions">
        <div className="search-field">
          <Search className="w-4 h-4" />
          <input
            type="text"
            placeholder={`Search ${title.toLowerCase()}`}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>
        <div className="sort-field">
          <div className="sort-label">Sort</div>
          <SelectMenu
            value={sort}
            options={sortOptions}
            placeholder={`Sort ${title.toLowerCase()}`}
            onChange={setSort}
          />
        </div>
      </div>
    </div>
  );
}

function InstructionProjectModal({ open, title, formState, setFormState, categories, onClose, onSubmit, saving }) {
  return (
    <Modal
      open={open}
      title={title}
      onClose={onClose}
      footer={
        <div className="modal-actions">
          <button className="btn-secondary" type="button" onClick={onClose}>Cancel</button>
          <button className="btn-primary" type="button" onClick={onSubmit} disabled={saving}>
            {title.startsWith('Edit') ? 'Save' : 'Create'}
          </button>
        </div>
      }
    >
      <div className="modal-field">
        <label>Name</label>
        <input
          className="input-glass"
          value={formState.name}
          onChange={(event) => setFormState((prev) => ({ ...prev, name: event.target.value }))}
          placeholder="Project name"
        />
      </div>
      <div className="modal-field">
        <label>Description</label>
        <textarea
          className="input-glass"
          rows="3"
          value={formState.description}
          onChange={(event) => setFormState((prev) => ({ ...prev, description: event.target.value }))}
          placeholder="Optional description"
        />
      </div>
      <div className="modal-field">
        <label>Category</label>
        <SelectMenu
          value={formState.category_id}
          options={categories.map((category) => ({ value: category.category_id, label: category.name }))}
          placeholder="Select a category"
          onChange={(value) => setFormState((prev) => ({ ...prev, category_id: value }))}
        />
      </div>
      <div className="modal-field">
        <label>Accent Color</label>
        <ColorPicker value={formState.color} onChange={(color) => setFormState((prev) => ({ ...prev, color }))} />
      </div>
      <div className="modal-field">
        <label>Visibility</label>
        <SelectMenu
          value={formState.visibility}
          options={[
            { value: 'private', label: 'Private', description: 'Only you can access this project.' },
            { value: 'public', label: 'Public', description: 'Visible when you later expose this project.' }
          ]}
          placeholder="Select visibility"
          onChange={(value) => setFormState((prev) => ({ ...prev, visibility: value }))}
        />
      </div>
    </Modal>
  );
}

function InstructionFolderModal({ open, title, formState, setFormState, categories, onClose, onSubmit, saving }) {
  return (
    <Modal
      open={open}
      title={title}
      onClose={onClose}
      footer={
        <div className="modal-actions">
          <button className="btn-secondary" type="button" onClick={onClose}>Cancel</button>
          <button className="btn-primary" type="button" onClick={onSubmit} disabled={saving}>
            {title.startsWith('Edit') ? 'Save' : 'Create'}
          </button>
        </div>
      }
    >
      <div className="modal-field">
        <label>Name</label>
        <input
          className="input-glass"
          value={formState.name}
          onChange={(event) => setFormState((prev) => ({ ...prev, name: event.target.value }))}
          placeholder="Folder name"
        />
      </div>
      <div className="modal-field">
        <label>Description</label>
        <textarea
          className="input-glass"
          rows="3"
          value={formState.description}
          onChange={(event) => setFormState((prev) => ({ ...prev, description: event.target.value }))}
          placeholder="Optional description"
        />
      </div>
      <div className="modal-field">
        <label>Category</label>
        <SelectMenu
          value={formState.category_id}
          options={categories.map((category) => ({ value: category.category_id, label: category.name }))}
          placeholder="Select a category"
          onChange={(value) => setFormState((prev) => ({ ...prev, category_id: value }))}
        />
      </div>
      <div className="modal-field">
        <label>Accent Color</label>
        <ColorPicker value={formState.color} onChange={(color) => setFormState((prev) => ({ ...prev, color }))} />
      </div>
      <div className="modal-field">
        <label>Visibility</label>
        <SelectMenu
          value={formState.visibility}
          options={[
            { value: 'private', label: 'Private', description: 'Only you can access this folder.' },
            { value: 'public', label: 'Public', description: 'Visible when you later expose this folder.' }
          ]}
          placeholder="Select visibility"
          onChange={(value) => setFormState((prev) => ({ ...prev, visibility: value }))}
        />
      </div>
    </Modal>
  );
}

export function InstructionProjectsIndexView() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('updated_desc');
  const [createOpen, setCreateOpen] = useState(false);
  const [editProject, setEditProject] = useState(null);
  const [deleteProject, setDeleteProject] = useState(null);
  const [deleteMode, setDeleteMode] = useState('trash_children');
  const [deleteTargetProjectId, setDeleteTargetProjectId] = useState('');
  const [formState, setFormState] = useState(DEFAULT_FORM_STATE);
  const [saving, setSaving] = useState(false);

  const sortedCategories = useMemo(
    () => [...(categories || [])].sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [categories]
  );

  const loadProjects = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [projectData, categoryData] = await Promise.all([
        apiClient.get('/api/instruction-projects'),
        apiClient.get('/api/categories')
      ]);
      setProjects(Array.isArray(projectData) ? projectData : []);
      setCategories(Array.isArray(categoryData) ? categoryData : []);
    } catch (err) {
      setError(err.message || 'Unable to load instruction projects.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  const filteredProjects = useMemo(() => {
    const filtered = projects.filter((project) => (project.name || '').toLowerCase().includes(search.toLowerCase()));
    const { pinnedItems, unpinned } = splitPinned(filtered, 'project_id');
    return [...pinnedItems, ...sortByOption(unpinned, sort)];
  }, [projects, search, sort]);

  const openCreate = () => {
    setFormState(DEFAULT_FORM_STATE);
    setCreateOpen(true);
  };

  const handleCreate = async () => {
    if (!formState.name.trim()) return;
    if (!formState.category_id) {
      setError('Select a category before creating a project.');
      return;
    }
    setSaving(true);
    try {
      const saved = await apiClient.post('/api/instruction-projects', formState);
      setProjects((prev) => [saved, ...prev]);
      setCreateOpen(false);
    } catch (err) {
      setError(err.message || 'Unable to create instruction project.');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!editProject) return;
    if (!formState.name.trim()) return;
    if (!formState.category_id) {
      setError('Select a category before saving the project.');
      return;
    }
    setSaving(true);
    try {
      const saved = await apiClient.patch(`/api/instruction-projects/${editProject.project_id}`, formState);
      setProjects((prev) => prev.map((item) => (item.project_id === editProject.project_id ? saved : item)));
      setEditProject(null);
    } catch (err) {
      setError(err.message || 'Unable to update instruction project.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteProject) return;
    try {
      await apiClient.del(`/api/instruction-projects/${deleteProject.project_id}`, {
        mode: deleteMode,
        target_project_id: deleteMode === 'move_folders' ? deleteTargetProjectId : null
      });
      setProjects((prev) => prev.filter((item) => item.project_id !== deleteProject.project_id));
      setDeleteProject(null);
    } catch (err) {
      setError(err.message || 'Unable to delete instruction project.');
    }
  };

  const handleTogglePin = async (project) => {
    const nextPinnedAt = project.pinned_at ? null : new Date().toISOString();
    try {
      const saved = await apiClient.patch(`/api/instruction-projects/${project.project_id}`, { pinned_at: nextPinnedAt });
      setProjects((prev) => prev.map((item) => (item.project_id === project.project_id ? saved : item)));
    } catch (err) {
      setError(err.message || 'Unable to update pin.');
    }
  };

  return (
    <div className="min-h-screen projects-page">
      <WorkspaceHeader
        breadcrumbs={[{ label: 'Agent Instructions' }]}
        title="Instruction Projects"
        description="Organize agent instructions in their own dedicated projects and folders."
        search={search}
        setSearch={setSearch}
        sort={sort}
        setSort={setSort}
        sortOptions={SORT_OPTIONS.projects}
        actionLabel="New Project"
        onAction={openCreate}
      />

      {error && <div className="error-banner">{error}</div>}

      {loading ? (
        <div className="spinner"></div>
      ) : filteredProjects.length === 0 ? (
        <EmptyState
          title="No instruction projects yet"
          description="Create your first instruction project to organize folders and agent instruction systems."
          actionLabel="Create Project"
          onAction={openCreate}
        />
      ) : (
        <div className="tile-grid">
          {filteredProjects.map((project) => (
            <ProjectCard
              key={project.project_id}
              project={project}
              pinned={!!project.pinned_at}
              onOpen={() => navigate(`/agent-instructions/projects/${project.project_id}`)}
              onEdit={() => {
                setFormState({
                  name: project.name,
                  description: project.description || '',
                  category_id: project.category_id || '',
                  color: project.color || '#3B82F6',
                  visibility: project.visibility || 'private'
                });
                setEditProject(project);
              }}
              onTogglePin={() => handleTogglePin(project)}
              onDelete={() => {
                setDeleteProject(project);
                setDeleteMode('trash_children');
                setDeleteTargetProjectId('');
              }}
            />
          ))}
        </div>
      )}

      <InstructionProjectModal
        open={createOpen || !!editProject}
        title={editProject ? 'Edit Instruction Project' : 'Create Instruction Project'}
        formState={formState}
        setFormState={setFormState}
        categories={sortedCategories}
        onClose={() => {
          setCreateOpen(false);
          setEditProject(null);
        }}
        onSubmit={editProject ? handleUpdate : handleCreate}
        saving={saving}
      />

      <Modal
        open={!!deleteProject}
        title="Move Instruction Project To Trash?"
        onClose={() => setDeleteProject(null)}
        footer={
          <div className="modal-actions">
            <button className="btn-secondary" type="button" onClick={() => setDeleteProject(null)}>Cancel</button>
            <button
              className="btn-primary danger"
              type="button"
              onClick={handleDelete}
              disabled={deleteMode === 'move_folders' && !deleteTargetProjectId}
            >
              Move to Trash
            </button>
          </div>
        }
      >
        <div className="space-y-3 text-gray-300">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="delete-instruction-project-mode"
              checked={deleteMode === 'trash_children'}
              onChange={() => setDeleteMode('trash_children')}
            />
            Trash this instruction project and all folders/instructions inside it.
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="delete-instruction-project-mode"
              checked={deleteMode === 'move_folders'}
              onChange={() => setDeleteMode('move_folders')}
            />
            Move folders and instructions to another instruction project, then trash this project.
          </label>
          {deleteMode === 'move_folders' && (
            <div className="modal-field">
              <label>Move To Project</label>
              <SelectMenu
                value={deleteTargetProjectId}
                options={projects
                  .filter((item) => item.project_id !== deleteProject?.project_id)
                  .map((item) => ({ value: item.project_id, label: item.name }))}
                placeholder="Select destination project"
                onChange={setDeleteTargetProjectId}
              />
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
}

export function InstructionProjectFoldersView() {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [folders, setFolders] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('updated_desc');
  const [createOpen, setCreateOpen] = useState(false);
  const [editFolder, setEditFolder] = useState(null);
  const [deleteFolder, setDeleteFolder] = useState(null);
  const [formState, setFormState] = useState(DEFAULT_FORM_STATE);
  const [saving, setSaving] = useState(false);

  const sortedCategories = useMemo(
    () => [...(categories || [])].sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [categories]
  );

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [projectData, folderData, categoryData] = await Promise.all([
        apiClient.get(`/api/instruction-projects/${projectId}`),
        apiClient.get('/api/instruction-folders', { project_id: projectId }),
        apiClient.get('/api/categories')
      ]);
      setProject(projectData && projectData.project_id ? projectData : null);
      setFolders(Array.isArray(folderData) ? folderData : []);
      setCategories(Array.isArray(categoryData) ? categoryData : []);
    } catch (err) {
      setError(err.message || 'Unable to load instruction folders.');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filteredFolders = useMemo(() => {
    const filtered = folders.filter((folder) => (folder.name || '').toLowerCase().includes(search.toLowerCase()));
    const { pinnedItems, unpinned } = splitPinned(filtered, 'folder_id');
    return [...pinnedItems, ...sortByOption(unpinned, sort)];
  }, [folders, search, sort]);

  const openCreate = () => {
    setFormState(DEFAULT_FORM_STATE);
    setCreateOpen(true);
  };

  const handleCreate = async () => {
    if (!formState.name.trim()) return;
    if (!formState.category_id) {
      setError('Select a category before creating a folder.');
      return;
    }
    setSaving(true);
    try {
      const saved = await apiClient.post('/api/instruction-folders', { ...formState, project_id: projectId });
      setFolders((prev) => [saved, ...prev]);
      setCreateOpen(false);
    } catch (err) {
      setError(err.message || 'Unable to create instruction folder.');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!editFolder) return;
    if (!formState.name.trim()) return;
    if (!formState.category_id) {
      setError('Select a category before saving the folder.');
      return;
    }
    setSaving(true);
    try {
      const saved = await apiClient.patch(`/api/instruction-folders/${editFolder.folder_id}`, formState);
      setFolders((prev) => prev.map((item) => (item.folder_id === editFolder.folder_id ? saved : item)));
      setEditFolder(null);
    } catch (err) {
      setError(err.message || 'Unable to update instruction folder.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteFolder) return;
    try {
      await apiClient.del(`/api/instruction-folders/${deleteFolder.folder_id}`);
      setFolders((prev) => prev.filter((item) => item.folder_id !== deleteFolder.folder_id));
      setDeleteFolder(null);
    } catch (err) {
      setError(err.message || 'Unable to delete instruction folder.');
    }
  };

  const handleTogglePin = async (folder) => {
    const nextPinnedAt = folder.pinned_at ? null : new Date().toISOString();
    try {
      const saved = await apiClient.patch(`/api/instruction-folders/${folder.folder_id}`, { pinned_at: nextPinnedAt });
      setFolders((prev) => prev.map((item) => (item.folder_id === folder.folder_id ? saved : item)));
    } catch (err) {
      setError(err.message || 'Unable to update pin.');
    }
  };

  return (
    <div className="min-h-screen projects-page">
      <WorkspaceHeader
        breadcrumbs={[
          { label: 'Agent Instructions', to: '/agent-instructions' },
          { label: project?.name || 'Instruction Project' }
        ]}
        title="Instruction Folders"
        description="Create folders inside this instruction project and keep related agent instructions together."
        search={search}
        setSearch={setSearch}
        sort={sort}
        setSort={setSort}
        sortOptions={SORT_OPTIONS.folders}
        actionLabel="New Folder"
        onAction={openCreate}
      />

      {error && <div className="error-banner">{error}</div>}

      {loading ? (
        <div className="spinner"></div>
      ) : filteredFolders.length === 0 ? (
        <EmptyState
          title="No instruction folders yet"
          description="Create a folder to group related agent instructions inside this project."
          actionLabel="Create Folder"
          onAction={openCreate}
        />
      ) : (
        <div className="tile-grid">
          {filteredFolders.map((folder) => (
            <FolderCard
              key={folder.folder_id}
              folder={folder}
              pinned={!!folder.pinned_at}
              onOpen={() => navigate(`/agent-instructions/projects/${projectId}/folders/${folder.folder_id}`)}
              onEdit={() => {
                setFormState({
                  name: folder.name,
                  description: folder.description || '',
                  category_id: folder.category_id || '',
                  color: folder.color || '#3B82F6',
                  visibility: folder.visibility || 'private'
                });
                setEditFolder(folder);
              }}
              onTogglePin={() => handleTogglePin(folder)}
              onDelete={() => setDeleteFolder(folder)}
            />
          ))}
        </div>
      )}

      <InstructionFolderModal
        open={createOpen || !!editFolder}
        title={editFolder ? 'Edit Instruction Folder' : 'Create Instruction Folder'}
        formState={formState}
        setFormState={setFormState}
        categories={sortedCategories}
        onClose={() => {
          setCreateOpen(false);
          setEditFolder(null);
        }}
        onSubmit={editFolder ? handleUpdate : handleCreate}
        saving={saving}
      />

      <Modal
        open={!!deleteFolder}
        title="Move Instruction Folder To Trash?"
        onClose={() => setDeleteFolder(null)}
        footer={
          <div className="modal-actions">
            <button className="btn-secondary" type="button" onClick={() => setDeleteFolder(null)}>Cancel</button>
            <button className="btn-primary danger" type="button" onClick={handleDelete}>Move to Trash</button>
          </div>
        }
      >
        <p className="text-gray-300">
          This instruction folder and all nested agent instructions will be moved to trash.
        </p>
      </Modal>
    </div>
  );
}

export function InstructionFolderInstructionsView() {
  const navigate = useNavigate();
  const { projectId, folderId } = useParams();
  const [project, setProject] = useState(null);
  const [folder, setFolder] = useState(null);
  const [instructions, setInstructions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('updated_desc');
  const [deleteInstruction, setDeleteInstruction] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [projectData, folderData, instructionData] = await Promise.all([
        apiClient.get(`/api/instruction-projects/${projectId}`),
        apiClient.get(`/api/instruction-folders/${folderId}`),
        apiClient.get('/api/agent-instructions', { folder_id: folderId })
      ]);
      setProject(projectData && projectData.project_id ? projectData : null);
      setFolder(folderData && folderData.folder_id ? folderData : null);
      setInstructions(Array.isArray(instructionData) ? instructionData : []);
    } catch (err) {
      setError(err.message || 'Unable to load agent instructions.');
    } finally {
      setLoading(false);
    }
  }, [folderId, projectId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filteredInstructions = useMemo(() => {
    const filtered = instructions.filter((instruction) => {
      const term = search.toLowerCase();
      const name = (instruction.name || '').toLowerCase();
      const description = (instruction.description || '').toLowerCase();
      return name.includes(term) || description.includes(term);
    });
    const { pinnedItems, unpinned } = splitPinned(filtered, 'instruction_id');
    return [...pinnedItems, ...sortByOption(unpinned, sort)];
  }, [instructions, search, sort]);

  const handleTogglePin = async (instruction) => {
    const nextPinnedAt = instruction.pinned_at ? null : new Date().toISOString();
    try {
      const saved = await apiClient.patch(`/api/agent-instructions/${instruction.instruction_id}`, { pinned_at: nextPinnedAt });
      setInstructions((prev) => prev.map((item) => (item.instruction_id === instruction.instruction_id ? saved : item)));
    } catch (err) {
      setError(err.message || 'Unable to update pin.');
    }
  };

  const handleDelete = async () => {
    if (!deleteInstruction) return;
    try {
      await apiClient.del(`/api/agent-instructions/${deleteInstruction.instruction_id}`);
      setInstructions((prev) => prev.filter((item) => item.instruction_id !== deleteInstruction.instruction_id));
      setDeleteInstruction(null);
    } catch (err) {
      setError(err.message || 'Unable to delete instruction.');
    }
  };

  return (
    <div className="min-h-screen projects-page">
      <WorkspaceHeader
        breadcrumbs={[
          { label: 'Agent Instructions', to: '/agent-instructions' },
          { label: project?.name || 'Instruction Project', to: `/agent-instructions/projects/${projectId}` },
          { label: folder?.name || 'Instruction Folder' }
        ]}
        title="Agent Instructions"
        description="Create, manage, and version instructions inside this folder."
        search={search}
        setSearch={setSearch}
        sort={sort}
        setSort={setSort}
        sortOptions={SORT_OPTIONS.instructions}
        actionLabel="Create Instruction"
        onAction={() => navigate('/agent-instructions/create', {
          state: {
            projectId,
            folderId,
            projectName: project?.name,
            folderName: folder?.name
          }
        })}
      />

      {error && <div className="error-banner">{error}</div>}

      {loading ? (
        <div className="spinner"></div>
      ) : filteredInstructions.length === 0 ? (
        <EmptyState
          title="No agent instructions yet"
          description="Create an instruction in this folder to start building organized agent systems."
          actionLabel="Create Instruction"
          onAction={() => navigate('/agent-instructions/create', {
            state: {
              projectId,
              folderId,
              projectName: project?.name,
              folderName: folder?.name
            }
          })}
        />
      ) : (
        <div className="tile-grid">
          {filteredInstructions.map((instruction) => (
            <InstructionCard
              key={instruction.instruction_id}
              instruction={instruction}
              onOpen={() => navigate(`/agent-instructions/${instruction.instruction_id}`, {
                state: {
                  backTo: `/agent-instructions/projects/${projectId}/folders/${folderId}`
                }
              })}
              onEdit={() => navigate(`/agent-instructions/${instruction.instruction_id}/edit`, {
                state: {
                  backTo: `/agent-instructions/projects/${projectId}/folders/${folderId}`
                }
              })}
              onTogglePin={() => handleTogglePin(instruction)}
              onDelete={() => setDeleteInstruction(instruction)}
            />
          ))}
        </div>
      )}

      <Modal
        open={!!deleteInstruction}
        title="Move Instruction To Trash?"
        onClose={() => setDeleteInstruction(null)}
        footer={
          <div className="modal-actions">
            <button className="btn-secondary" type="button" onClick={() => setDeleteInstruction(null)}>Cancel</button>
            <button className="btn-primary danger" type="button" onClick={handleDelete}>Move to Trash</button>
          </div>
        }
      >
        <p className="text-gray-300">
          This instruction will be moved to trash and removed from the folder.
        </p>
      </Modal>
    </div>
  );
}
